import { useEffect, useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { InquiryType } from '../services/contactService';

interface ThankYouModalProps {
  isOpen: boolean;
  onClose: () => void;
  inquiryType: InquiryType;
}

const inquiryTypeLabels: Record<InquiryType, string> = {
  general_inquiry: 'General Inquiry',
  general_support: 'General Support',
  sales_questions: 'Sales Question',
  work_with_us: 'Work With Us'
};

export function ThankYouModal({ isOpen, onClose, inquiryType }: ThankYouModalProps) {
  const [countdown, setCountdown] = useState(8);

  useEffect(() => {
    if (!isOpen) {
      setCountdown(8);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Thank You for Your Submission!
          </h2>

          <p className="text-gray-600 mb-4">
            We respond to all submissions within 24 hours.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              Your <span className="font-semibold">{inquiryTypeLabels[inquiryType]}</span> has been received and our team will review it shortly.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={onClose}
              className="w-full bg-blue-600 text-white font-medium py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Another Inquiry
            </button>

            <p className="text-xs text-gray-500">
              This window will close automatically in {countdown} seconds
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
